<template>
  <button
    type="button"
    class="kds-sidebar-panel-row"
    :class="{ 'is-active': isActive }"
    @click="$emit('click', id)"
  >
    <span
      v-if="isLive"
      class="kds-sidebar-panel-row__dot kds-pulse-dot"
      aria-hidden="true"
    />
    <span class="kds-sidebar-panel-row__body">
      <span class="kds-sidebar-panel-row__name">{{ name }}</span>
      <span class="kds-sidebar-panel-row__meta">{{ protocol }} · {{ personaLabel }}</span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  id: string;
  name: string;
  protocol: string;
  personaCount: number;
  isActive?: boolean;
  isLive?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isActive: false,
  isLive: false
});

defineEmits<{
  (e: 'click', id: string): void;
}>();

const personaLabel = computed(() =>
  props.personaCount === 1 ? '1 persona' : `${props.personaCount} personas`
);
</script>

<style scoped lang="scss">
.kds-sidebar-panel-row {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  border-radius: var(--r-2);
  transition: background-color 120ms var(--ease-out);

  &:hover {
    background: var(--paper-3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &.is-active {
    .kds-sidebar-panel-row__name {
      color: var(--ink);
      font-weight: var(--fw-semibold);
    }
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: var(--r-pill);
    background: var(--signal);
    flex-shrink: 0;
    margin-top: 5px;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }

  &__name {
    font-size: var(--fs-body);
    font-weight: var(--fw-semibold);
    color: var(--ink);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__meta {
    font-size: var(--fs-body-sm);
    font-family: var(--font-mono);
    color: var(--stone);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
